import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import DiagnoseAssistantTab from '../components/DiagnoseAssistantTab';
import ToolQualitySummary from '../components/tool-quality/ToolQualitySummary';
import ToolContentStudio from '../components/tool-content/ToolContentStudio';
import OptionMenu from '../components/tools-catalog/OptionMenu';
import {
    AdminTool, CATALOG_FIELD, FieldDef, STATUS_FIELD, TYPE_FIELD, ToolIcon, VISIBILITY_FIELD, byCatalogOrder, moduleOf, optionLabel, segmentState,
} from '../components/tools-catalog/catalogModel';
import OverviewSection, { ToolOverview } from '../components/tool-detail-page/OverviewSection';
import LookSection from '../components/tool-detail-page/LookSection';
import AccessSection from '../components/tool-detail-page/AccessSection';
import GuidePdfCard from '../components/tool-detail-page/GuidePdfCard';
import { SECTIONS, SectionId, sectionFrom } from '../components/tool-detail-page/sections';
import '../styles/tools-catalog.scss';
import '../styles/tool-detail.scss';

const HEADER_FIELDS: FieldDef[] = [STATUS_FIELD, VISIBILITY_FIELD, CATALOG_FIELD];
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL?.replace(/\/$/, '');
const dateFormat = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

/** Dettaglio di un tool: intestazione con stato e visibilità, sezioni a sinistra, navigazione tra tool. */
const ToolEditPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const toolId = Number(id);
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const section = sectionFrom(searchParams.get('tab'));

    const [tools, setTools] = useState<AdminTool[]>([]);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState<ToolOverview | null>(null);
    const [visited, setVisited] = useState<SectionId[]>([section]);
    const [overviewDirty, setOverviewDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadTools = useCallback(async () => {
        try {
            setTools((await apiService.adminGetTools<AdminTool>()).sort(byCatalogOrder));
        } catch {
            showToast('Tool non caricati', 'Verifica i permessi di amministratore', 'danger');
        } finally {
            setLoading(false);
        }
    }, [showToast]);
    useEffect(() => { void loadTools(); }, [loadTools]);

    useEffect(() => {
        setOverview(null);
        apiService.adminGetToolOverview<ToolOverview>(toolId).then(setOverview).catch(() => setOverview(null));
    }, [toolId]);

    // Ogni sezione aperta resta montata: si torna indietro senza perdere bozze o ricerche.
    useEffect(() => { setVisited(prev => (prev.includes(section) ? prev : [...prev, section])); }, [section]);
    // Cambiando tool si riparte dalla sola sezione aperta.
    const [shownToolId, setShownToolId] = useState(toolId);
    if (shownToolId !== toolId) {
        setShownToolId(toolId);
        setVisited([section]);
        setOverviewDirty(false);
    }

    const index = tools.findIndex(t => t.id === toolId);
    const tool = index >= 0 ? tools[index] : null;
    const previous = index > 0 ? tools[index - 1] : null;
    const next = index >= 0 && index < tools.length - 1 ? tools[index + 1] : null;

    const patchTool = useCallback((patch: Partial<AdminTool>) =>
        setTools(prev => prev.map(t => (t.id === toolId ? { ...t, ...patch } : t))), [toolId]);

    const confirmLeave = () => !overviewDirty || window.confirm('Ci sono modifiche non salvate ai dati del tool. Vuoi lasciarle?');

    const go = (target: SectionId) => {
        if (target === 'panoramica' && section !== 'panoramica') void loadTools(); // lo stato dei contenuti può essere cambiato
        setSearchParams({ tab: target }, { replace: true });
        window.scrollTo({ top: 0 });
    };
    const openTool = (other: AdminTool) => {
        if (!confirmLeave()) return;
        navigate(`/tools/${other.id}?tab=${section}`);
    };

    const saveHeader = async (field: FieldDef, value: string) => {
        if (!tool) return;
        const before = tool[field.key];
        patchTool({ [field.key]: value });
        setSaving(true);
        try {
            await apiService.adminUpdateTool(tool.id, { [field.key]: value });
            showToast(`${field.label}: ${optionLabel(field, value)}`, tool.name, 'success');
        } catch (err) {
            patchTool({ [field.key]: before });
            const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Riprova';
            showToast('Modifica non salvata', message, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const badges = useMemo((): Record<SectionId, string> => {
        const empty = { panoramica: '', contenuti: '', aspetto: '', accesso: '', qualita: '' };
        if (!tool) return empty;
        const states = (['publicPage', 'card', 'guide'] as const).map(k => segmentState(tool, k));
        const drafts = states.filter(s => s === 'draft').length;
        const issues = overview ? overview.errors24h + overview.openReports : 0;
        return {
            ...empty,
            contenuti: states.includes('outdated') ? 'da aggiornare' : drafts ? (drafts === 1 ? '1 bozza' : `${drafts} bozze`) : '',
            accesso: tool.visibility === 'Restricted' ? String(tool.assignedUserCount) : '',
            qualita: issues ? String(issues) : '',
        };
    }, [tool, overview]);

    if (loading) return <div className="text-center p-5"><span className="spinner-border" /></div>;
    if (!tool) return (
        <div className="alert alert-warning">
            Tool non trovato. <Link to="/tools">Torna al catalogo</Link>
        </div>
    );

    return (
        <div className="td">
            <div className="td-topnav">
                <Link to="/tools" className="td-back" onClick={e => { if (!confirmLeave()) e.preventDefault(); }}>
                    <i className="bi bi-chevron-left" aria-hidden="true" />Catalogo tool
                </Link>
                <div className="td-pager">
                    <span>Tool {index + 1} di {tools.length}</span>
                    <button type="button" className="td-pager__btn" disabled={!previous} onClick={() => previous && openTool(previous)}
                        aria-label={previous ? `Tool precedente: ${previous.name}` : 'Nessun tool precedente'} title={previous?.name}>
                        <i className="bi bi-chevron-left" aria-hidden="true" />
                    </button>
                    <button type="button" className="td-pager__btn" disabled={!next} onClick={() => next && openTool(next)}
                        aria-label={next ? `Tool successivo: ${next.name}` : 'Nessun tool successivo'} title={next?.name}>
                        <i className="bi bi-chevron-right" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <header className="td-head">
                <div className="td-head__main">
                <ToolIcon tool={tool} size={64} />
                <div className="td-head__text">
                    <h1>{tool.name}</h1>
                    <div className="td-head__meta">
                        <code>{tool.code}</code>
                        <span>{optionLabel(TYPE_FIELD, tool.toolType)} · {moduleOf(tool.code)}</span>
                        {overview && <span>Creato il {dateFormat.format(new Date(overview.createdAt))}</span>}
                    </div>
                </div>
                <div className="td-head__actions">
                    {HEADER_FIELDS.map(field => {
                        const current = field.options.find(o => o.value === tool[field.key]);
                        const extra = field.key === 'visibility' && tool.visibility === 'Restricted' ? ` (${tool.assignedUserCount})` : '';
                        return (
                            <OptionMenu
                                key={field.key}
                                options={field.options}
                                value={tool[field.key]}
                                onPick={value => void saveHeader(field, value)}
                                triggerClassName="cat-cell td-head__menu"
                                ariaLabel={`${field.label}: ${current?.label ?? tool[field.key]}. Cambia`}
                                disabled={saving}
                                trigger={<>
                                    <span className={field.pill ? `cat-pill cat-pill--${current?.tone ?? 'muted'}` : 'cat-plain'}>
                                        {current?.dot && <span className="cat-dot" style={{ background: current.dot }} aria-hidden="true" />}
                                        {current?.label ?? tool[field.key]}{extra}
                                    </span>
                                    <i className="bi bi-chevron-down cat-cell__caret" aria-hidden="true" />
                                </>}
                            />
                        );
                    })}
                    {PORTAL_URL && (
                        <a className="td-head__portal" href={`${PORTAL_URL}/dashboard/tools`} target="_blank" rel="noopener noreferrer">
                            Apri nel portale<i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                            <span className="visually-hidden"> (si apre in una nuova scheda)</span>
                        </a>
                    )}
                </div>
                </div>
                <nav className="td-tabs" aria-label="Sezioni del tool">
                    {SECTIONS.map(s => (
                        <button key={s.id} type="button" className={`td-tabs__item${section === s.id ? ' is-on' : ''}`}
                            aria-current={section === s.id ? 'page' : undefined} onClick={() => go(s.id)}>
                            {s.label}
                            {badges[s.id] && <span className={`td-tabs__badge td-tabs__badge--${s.id}`}>{badges[s.id]}</span>}
                        </button>
                    ))}
                </nav>
            </header>

            <div className="td-panel">
                    {visited.includes('panoramica') && (
                        <div hidden={section !== 'panoramica'}>
                            <OverviewSection key={tool.id} tool={tool} overview={overview} onSaved={patchTool} onGo={go} onDirtyChange={setOverviewDirty} />
                        </div>
                    )}
                    {visited.includes('contenuti') && (
                        <div hidden={section !== 'contenuti'} className="td-contents">
                            <ToolContentStudio key={tool.id} toolId={tool.id} toolStatus={tool.status} toolVisibility={tool.visibility}
                                guideExtra={<GuidePdfCard toolId={tool.id} onLanguagesChange={languages => patchTool({ guideLanguages: languages })} />} />
                        </div>
                    )}
                    {visited.includes('aspetto') && (
                        <div hidden={section !== 'aspetto'}>
                            <LookSection key={tool.id} tool={tool} onImagesChanged={roles => patchTool({ imageRoles: roles })} />
                        </div>
                    )}
                    {visited.includes('accesso') && (
                        <div hidden={section !== 'accesso'}>
                            <AccessSection key={tool.id} tool={tool} onCountChange={count => patchTool({ assignedUserCount: count })} />
                        </div>
                    )}
                    {visited.includes('qualita') && (
                        <div hidden={section !== 'qualita'} className="td-quality">
                            <ToolQualitySummary toolId={tool.id} toolCode={tool.code} toolName={tool.name}
                                openReportCount={tool.openReportCount} isDebug={tool.status === 'Debug'} />
                            <section className="td-card" aria-labelledby="td-assistant-title">
                                <h2 id="td-assistant-title" className="td-card__title">Assistente per il triage</h2>
                                <p className="td-card__lead">Fa domande sugli eventi e sulle segnalazioni di questo tool.</p>
                                <DiagnoseAssistantTab toolCode={tool.code} />
                            </section>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default ToolEditPage;
